/**
 * pages/admin/AdminHealthCheck.tsx
 *
 * Admin-only health check page accessible at /admin/health-check.
 * Runs a series of critical Supabase queries to verify that key RLS policies,
 * data access patterns, and security hardening are intact after a deployment.
 *
 * Checks performed:
 *   1.  Auth — current user profile is readable
 *   2.  Forms visibility — user can read form_templates (RLS on form_templates)
 *   3.  Admin forms visibility — admin-created forms (created_by NOT NULL) are readable
 *   4.  Assigned forms — form_template_assignments table is accessible
 *   5.  Clients — clients table is accessible and scoped to current user
 *   6.  Documents — documents table is accessible
 *   7.  Shared links — shared_links table is accessible
 *   8.  Requests — requests table is accessible
 *   9.  FK user delete integrity — all FK chains referencing users use CASCADE or SET NULL
 *   10. Analytics view vw_user_activity_summary — security_invoker view readable by admin
 *   11. Analytics view vw_admin_activity_events — security_invoker view readable by admin
 *   12. SECURITY DEFINER grants — no public schema SECURITY DEFINER function exposes EXECUTE to anon/PUBLIC
 *   13. Function search_path — all PL/pgSQL public functions have a pinned search_path
 *   14. Storage broad SELECT policies — no public-role SELECT policy on storage.objects
 *   15. SecDef exception justifications — every approved exception has INTENTIONAL: comment in pg catalog
 *   16. SecDef authenticated grants — no unapproved SECURITY DEFINER function exposed to authenticated
 *   17. RLS tables without policies — no table with RLS on and 0 policies outside the approved allowlist
 *
 * Each check reports: OK (green), WARNING (amber), or ERROR (red).
 * Run this page 30 seconds after every deployment.
 *
 * Updated: 2026-05-11 — refactored into sub-modules (checks.ts, runners.ts,
 *   StatusBadge.tsx, types.ts); added checks #12-#14 (security audit regression
 *   detection) following the SECURITY DEFINER / storage listing audit hardening.
 * Updated: 2026-05-12 — added check #15 (secdef_exception_justifications) to detect
 *   when approved SECURITY DEFINER exceptions lose their INTENTIONAL: pg_description.
 *   Added check #16 (secdef_authenticated_grants) to detect new unapproved SECURITY
 *   DEFINER functions exposed to authenticated.
 *   Added check #17 (rls_tables_without_policies) to detect new tables with RLS on
 *   but no policies outside the approved service-role-only allowlist.
 */

import { useState, useCallback } from 'react';
import AppLayout from '../../components/layout/AppLayout';
import { useAuth } from '../../contexts/AuthContext';
import { RefreshCw, ShieldCheck } from 'lucide-react';
import { INITIAL_CHECKS } from './healthCheck/checks';
import { StatusIcon, statusBg, statusLabel, statusLabelColor, statusDetailColor } from './healthCheck/StatusBadge';
import type { HealthCheck } from './healthCheck/types';
import {
  runAuthCheck,
  runFormTemplatesOwnCheck,
  runFormTemplatesAdminCheck,
  runAssignmentsCheck,
  runClientsCheck,
  runDocumentsCheck,
  runSharedLinksCheck,
  runRequestsCheck,
  runFkIntegrityCheck,
  runActivitySummaryCheck,
  runActivityEventsCheck,
  runSecDefGrantsCheck,
  runFunctionSearchPathCheck,
  runStoragePoliciesCheck,
  runSecDefJustificationCheck,
  runSecDefAuthenticatedGrantsCheck,
  runRlsTablesWithoutPoliciesCheck,
} from './healthCheck/runners';

export default function AdminHealthCheck() {
  const { user } = useAuth();
  const [checks, setChecks] = useState<HealthCheck[]>(INITIAL_CHECKS);
  const [running, setRunning] = useState(false);
  const [lastRunAt, setLastRunAt] = useState<Date | null>(null);

  const updateCheck = useCallback((id: string, patch: Partial<HealthCheck>) => {
    setChecks((prev) => prev.map((c) => (c.id === id ? { ...c, ...patch } : c)));
  }, []);

  const runChecks = useCallback(async () => {
    if (!user) return;
    setRunning(true);
    setChecks(INITIAL_CHECKS.map((c) => ({ ...c, status: 'pending', detail: null })));

    try {
      await runAuthCheck(user, updateCheck);
      await runFormTemplatesOwnCheck(user, updateCheck);
      await runFormTemplatesAdminCheck(user, updateCheck);
      await runAssignmentsCheck(user, updateCheck);
      await runClientsCheck(user, updateCheck);
      await runDocumentsCheck(user, updateCheck);
      await runSharedLinksCheck(user, updateCheck);
      await runRequestsCheck(user, updateCheck);
      await runFkIntegrityCheck(user, updateCheck);
      await runActivitySummaryCheck(user, updateCheck);
      await runActivityEventsCheck(user, updateCheck);
      await runSecDefGrantsCheck(user, updateCheck);
      await runFunctionSearchPathCheck(user, updateCheck);
      await runStoragePoliciesCheck(user, updateCheck);
      await runSecDefJustificationCheck(user, updateCheck);
      await runSecDefAuthenticatedGrantsCheck(user, updateCheck);
      await runRlsTablesWithoutPoliciesCheck(user, updateCheck);
      setLastRunAt(new Date());
    } finally {
      setRunning(false);
    }
  }, [user, updateCheck]);

  const summary = (() => {
    const errors = checks.filter((c) => c.status === 'error').length;
    const warnings = checks.filter((c) => c.status === 'warning').length;
    const ok = checks.filter((c) => c.status === 'ok').length;
    const total = checks.length;
    if (errors > 0) return { label: `${errors} erreur(s) détectée(s)`, color: 'text-error-700 bg-error-50 border-error-200' };
    if (warnings > 0) return { label: `${warnings} avertissement(s) — vérifier`, color: 'text-accent-700 bg-accent-50 border-accent-200' };
    if (ok === total) return { label: 'Tous les contrôles sont au vert', color: 'text-success-700 bg-success-50 border-success-200' };
    return { label: "En attente d'exécution", color: 'text-neutral-600 bg-neutral-50 border-neutral-200' };
  })();

  return (
    <AppLayout
      breadcrumbs={[
        { label: 'Qollect', path: '/dashboard' },
        { label: 'Monitoring', path: '/dashboard' },
        { label: 'Health Check' },
      ]}
      actions={
        <div className="flex items-center gap-3 ml-auto">
          {lastRunAt && (
            <span className="text-xs text-neutral-400 hidden sm:inline">
              Dernier contrôle : {lastRunAt.toLocaleTimeString('fr-FR')}
            </span>
          )}
          <button
            onClick={runChecks}
            disabled={running}
            className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white text-sm font-medium rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <RefreshCw className={`w-4 h-4 ${running ? 'animate-spin' : ''}`} />
            {running ? 'Contrôle en cours...' : 'Lancer le contrôle'}
          </button>
        </div>
      }
    >
      <div className="space-y-6">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 bg-primary-50 rounded-xl flex items-center justify-center flex-shrink-0">
            <ShieldCheck className="w-6 h-6 text-primary-600" />
          </div>
          <div>
            <h1 className="text-2xl font-semibold text-neutral-900">Health Check</h1>
            <p className="text-neutral-500 mt-1 text-sm">
              Vérifications automatiques des accès critiques et de la sécurité. A exécuter 30 secondes après chaque déploiement.
            </p>
          </div>
        </div>

        <div className={`rounded-xl border px-5 py-4 text-sm font-medium ${summary.color}`}>
          {summary.label}
        </div>

        <div className="space-y-3">
          {checks.map((check) => (
            <div
              key={check.id}
              className={`rounded-xl border px-5 py-4 transition-colors ${statusBg(check.status)}`}
            >
              <div className="flex items-center gap-3">
                <StatusIcon status={check.status} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-medium text-neutral-900">{check.label}</span>
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${statusLabelColor(check.status)}`}>
                      {statusLabel(check.status)}
                    </span>
                  </div>
                  <p className="text-xs text-neutral-500 mt-0.5">{check.description}</p>
                  {check.detail && (
                    <p className={`text-xs mt-1.5 font-mono ${statusDetailColor(check.status)}`}>
                      {check.detail}
                    </p>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        {!running && lastRunAt === null && (
          <div className="rounded-xl bg-neutral-50 border border-neutral-200 px-5 py-4">
            <p className="text-sm text-neutral-600">
              Cliquez sur <strong>Lancer le contrôle</strong> pour vérifier que toutes les politiques d'accès
              (RLS) et les règles de sécurité fonctionnent correctement. Cette page doit être consultée
              systématiquement après chaque déploiement.
            </p>
          </div>
        )}
      </div>
    </AppLayout>
  );
}
